/**
 * M7 S32: the Logs screen's state and reducer (keyboard and pad). Pure; the host carries out the effects.
 *
 * - **Zones:** the tab bar and the list. Tab bar: left/right switch tabs, down enters the list. List: up/down move
 *   (up from the top returns to the tab bar), left/right still switch tabs, activate presses the row.
 * - **Rows:** on *The case*, the open leads (activate = set nav target: the only guidance); on a source tab or
 *   *Messages*, the entries (activate opens the log; an unread one is marked read); on *Your records*, the
 *   generated logs. Tables and questions are read, not pressed.
 * - **An open log:** back (or activate) returns to the list. Back elsewhere closes the screen.
 */
import type { NavInput } from '../../strategic/mount'
import type { LogEntryView, LogsTabId, LogsView } from './logs'

export interface LogsScreenState {
  tab: LogsTabId
  zone: 'tabs' | 'list'
  row: number
  /** The open log's ledger id (null: the list). */
  openLog: string | null
  notice: string | null
}

export type LogsAction =
  | { type: 'nav'; input: NavInput }
  | { type: 'selectTab'; tab: LogsTabId }
  | { type: 'pressRow'; index: number }
  | { type: 'closeLog' }
  | { type: 'notice'; text: string | null }

export type LogsEffect =
  | { kind: 'read'; logId: string }
  | { kind: 'navTarget'; logId: string }
  | { kind: 'close' }
  /** M7 S34: open the claim panel (the case tab's draft row). */
  | { kind: 'claim' }
  /** M7 S39: open the Finale screen (the case tab's finale row). */
  | { kind: 'finale' }

export interface LogsStep {
  state: LogsScreenState
  effect: LogsEffect | null
}

export function createLogsState(): LogsScreenState {
  return { tab: 'case', zone: 'tabs', row: 0, openLog: null, notice: null }
}

/** M7 S34: the case tab's claim draft row id (first, before the leads). */
export const CLAIM_ROW = '#claim'
/** M7 S39: the case tab's finale row id (after the claim row, before the leads). */
export const FINALE_ROW = '#finale'

/** The pressable rows of `tab`: leads on the case tab, entries elsewhere. */
export function tabRows(view: LogsView, tab: LogsTabId): readonly { id: string; entry: LogEntryView | null }[] {
  if (tab === 'case') {
    const leads = view.leads.map((l) => ({ id: l.logId, entry: null }))
    const head: { id: string; entry: null }[] = []
    if (view.claimDraft != null) head.push({ id: CLAIM_ROW, entry: null })
    if (view.finaleRow != null) head.push({ id: FINALE_ROW, entry: null })
    return head.length === 0 ? leads : [...head, ...leads]
  }
  const list = tab === 'records' ? view.records.logs : (view.entries[tab] ?? [])
  return list.map((e) => ({ id: e.id, entry: e }))
}

/** The entry `id` on `tab`, if it is there. */
export function entryOn(view: LogsView, tab: LogsTabId, id: string): LogEntryView | null {
  return tabRows(view, tab).find((r) => r.id === id)?.entry ?? null
}

/** `s` with its tab, row and open log still valid for `view` (tabs come and go as logs arrive). */
export function clampState(s: LogsScreenState, view: LogsView): LogsScreenState {
  const tab = view.tabs.some((t) => t.id === s.tab) ? s.tab : 'case'
  const rows = tabRows(view, tab)
  const row = rows.length === 0 ? 0 : Math.min(s.row, rows.length - 1)
  const zone = rows.length === 0 ? 'tabs' : s.zone
  const openLog = s.openLog !== null && entryOn(view, tab, s.openLog) !== null ? s.openLog : null
  if (tab === s.tab && row === s.row && zone === s.zone && openLog === s.openLog) return s
  return { ...s, tab, row, zone, openLog }
}

function switchTab(s: LogsScreenState, view: LogsView, dir: 1 | -1): LogsScreenState {
  const i = view.tabs.findIndex((t) => t.id === s.tab)
  const n = view.tabs.length
  const tab = view.tabs[(i + dir + n) % n].id
  return { ...s, tab, zone: 'tabs', row: 0, openLog: null, notice: null }
}

function press(s: LogsScreenState, view: LogsView, index: number): LogsStep {
  const rows = tabRows(view, s.tab)
  const r = rows[index]
  if (r === undefined) return { state: s, effect: null }
  const state: LogsScreenState = { ...s, zone: 'list', row: index }
  if (r.id === CLAIM_ROW) return { state, effect: { kind: 'claim' } }
  if (r.id === FINALE_ROW) return { state, effect: { kind: 'finale' } }
  if (r.entry === null) return { state, effect: { kind: 'navTarget', logId: r.id } }
  return {
    state: { ...state, openLog: r.id, notice: null },
    effect: r.entry.unread ? { kind: 'read', logId: r.id } : null,
  }
}

export function reduceLogs(prev: LogsScreenState, a: LogsAction, view: LogsView): LogsStep {
  const s = clampState(prev, view)
  switch (a.type) {
    case 'notice':
      return { state: { ...s, notice: a.text }, effect: null }
    case 'selectTab':
      if (!view.tabs.some((t) => t.id === a.tab)) return { state: s, effect: null }
      return { state: { ...s, tab: a.tab, zone: 'tabs', row: 0, openLog: null, notice: null }, effect: null }
    case 'pressRow':
      return press(s, view, a.index)
    case 'closeLog':
      return { state: { ...s, openLog: null }, effect: null }
    case 'nav':
      break
  }
  const input = a.input
  if (s.openLog !== null) {
    if (input === 'back' || input === 'activate') return { state: { ...s, openLog: null }, effect: null }
    if (input === 'left' || input === 'right') return { state: switchTab(s, view, input === 'left' ? -1 : 1), effect: null }
    return { state: s, effect: null }
  }
  const rows = tabRows(view, s.tab)
  switch (input) {
    case 'back':
    case 'start':
      return { state: s, effect: { kind: 'close' } }
    case 'left':
    case 'right':
      return { state: switchTab(s, view, input === 'left' ? -1 : 1), effect: null }
    case 'down':
      if (rows.length === 0) return { state: s, effect: null }
      if (s.zone === 'tabs') return { state: { ...s, zone: 'list', row: 0 }, effect: null }
      return { state: { ...s, row: Math.min(rows.length - 1, s.row + 1) }, effect: null }
    case 'up':
      if (s.zone === 'tabs') return { state: s, effect: null }
      if (s.row === 0) return { state: { ...s, zone: 'tabs' }, effect: null }
      return { state: { ...s, row: s.row - 1 }, effect: null }
    case 'activate':
      if (s.zone === 'tabs') {
        if (rows.length === 0) return { state: s, effect: null }
        return { state: { ...s, zone: 'list', row: 0 }, effect: null }
      }
      return press(s, view, s.row)
  }
}
